<script lang="ts">
    import SVGIcon from "$/components/SVGIcon.svelte";

    import {ClusterStore, ClusterType} from "$/stores/ClusterStore";
    import { currentSelectionStore} from "$/stores/PageSelectStore";
    import {TenantStore} from "$/stores/TenantStore";
    import {TokenStore} from "$/stores/TokenStore";
    import {clickAction} from "$/utils/clickAction";
    import {getRole} from "$/utils/Utils";
    import {createEventDispatcher} from "svelte";
    import type {Topic} from "../types";
    import {addHoverClass, removeHoverClass} from "../utils";
    import {ActiveTopicStore, ActiveTopic, TopicAccessLevel} from "$/stores/extension/ActiveTopicStore";

    export let clusterIndex: number;
    export let topicIndex: number;
    export let topic: Topic;
    export let topicRefs: Record<string, HTMLElement>;
    export let effectivePricingPlan: number;
    export let optionSelected: (topic: Topic) => void;
    export let optionEditClicked: (topic: Topic) => any;
    export let optionArchiveClicked: (topic: Topic) => any;
    export let optionLeaveClicked: (topic: Topic) => any;
    export let optionDeleteClicked: (topic: Topic) => any;
    export let archiveTopic: (topic: Topic, archive: boolean) => any;

    $: isPersonalTenant = $TenantStore?.active?.personal;

    $: isActiveTeamWorkspace = !TenantStore.isPersonal() && !TenantStore.isExpired();
    $: userCanArchive = getRole(topic, $TokenStore.user?.id, $TenantStore?.tenants, $ClusterStore.clusters) === TopicAccessLevel.OWNER || isActiveTeamWorkspace;
    $: userCanDelete = getRole(topic, $TokenStore.user?.id, $TenantStore?.tenants, $ClusterStore.clusters) === TopicAccessLevel.OWNER;
    $: isSelected = $currentSelectionStore?.clusterIndex === clusterIndex && 
                    $currentSelectionStore?.topicIndex === topicIndex;
    const userCanEdit = getRole(topic, $TokenStore.user?.id, $TenantStore?.tenants, $ClusterStore.clusters) >= TopicAccessLevel.EDITOR;

    const dispatch = createEventDispatcher();

    function handleTopicSelection(topic: Topic) {
        dispatch("clearSearchTopicsInput");
        optionSelected(topic);
    }
</script>

<span class={isSelected ? 'page-select-option selected' : 'page-select-option'}
      on:mouseenter={addHoverClass}
      on:mouseleave={removeHoverClass}
      bind:this={topicRefs[`${clusterIndex}-${topicIndex}`]}>

    <span class="option-title" class:--selected={$ActiveTopic?.id===topic.id}>
        <div class="interactive-option-text-wrapper" use:clickAction={() => handleTopicSelection(topic)}>
            {topic.name} 

        </div>
    </span>

    {#if effectivePricingPlan !== -1}
        {#if topic.cluster.clusterType === ClusterType.SHARED_WITH_ME && isPersonalTenant}
            <button class="option-button" on:click={optionLeaveClicked(topic)}>
                <SVGIcon name="close-circle" size={16} height={16} />
            </button>
        {:else if userCanEdit}
            {#if !topic.archived}
                <button
                    class="option-button"
                    on:click={optionEditClicked(topic)}
                >
                    <SVGIcon name="pencil" size={16} height={16} />
                </button>
                {#if userCanArchive}
                    <button
                        class="option-button"
                        on:click={optionArchiveClicked(topic)}
                    >
                        <SVGIcon name="archive" size={16} height={16} />
                    </button>
                {/if}
            {:else}
                <button
                    class="option-button"
                    on:click={archiveTopic(topic, false)}
                >
                    <SVGIcon name="unarchive" size={16} height={16} />
                </button>
                {#if userCanDelete}
                    <button
                        class="option-button"
                        on:click={optionDeleteClicked(topic)}
                    >
                        <SVGIcon name="delete" size={16} height={16} />
                    </button>
                {/if}
            {/if}
        {/if}
    {/if}
</span>

<style>
    .page-select-option {
        @apply reset cursor-pointer shrink-0 select-none no-underline flex flex-row items-center pr-2 text-gray-800 border-solid border-b border-gray-200  !important;
    }

    .selected {
        @apply bg-gray-100 !important;
    }
    .page-select-option:hover {
        @apply bg-gray-100 !important;
    }

    .option-title {
        @apply reset h-8 flex-1 truncate text-ellipsis px-4 text-sm leading-8 !important;
    }

    .option-title.--selected {
        @apply font-bold !important;
    }

    .interactive-option-text-wrapper {
        @apply overflow-hidden text-ellipsis;
    }

    .option-button {
        @apply reset flex h-8 w-6 cursor-pointer flex-row items-center justify-center text-gray-400 opacity-0 hover:text-gray-800 !important;
    }

    .page-select-option:hover .option-button {
        @apply opacity-100 !important;
    }
</style>
